import { twMerge } from "tailwind-merge";
import { PageContainer } from "../../../../components/pageContainer/PageContainer";
import { ReactElement, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  ButtonSpinner,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  Icon,
  RadioSelector,
  RadioSelectorItem,
} from "@sellar-io/components";

import { InputLabel, InputContainer, InputSelect } from "@sellar-io/components";
import { NavigationBar } from "~/components/navigation/NavigationBar";
import { BUSINESS_INTEGRATION_SETTINGS } from "~/constants/routes";
import { useSupplierIntegrationValueList } from "~/hooks/api/v2/supplier/useSupplierIntegrationValuesList";
import { useSupplierUpdate } from "~/hooks/api/v2/supplier/useSupplierUpdate";
import { useXeroChartOfAccountsList } from "~/hooks/api/v2/xero/useXeroChartOfAccountsList";
import { useXeroContactsCSV } from "~/hooks/api/v2/xero/useXeroContactsCSV";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { IntegrationUpdateContacts } from "./IntegrationUpdateContacts";
import { SharedIntegrationConfirmContent } from "./SharedIntegrationConfirmContent";

export const IntegrationConfirmXero = (): ReactElement => {
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const updateSupplierMutation = useSupplierUpdate(business?.Supplier?.id ?? 0);
  const xeroContactsCsv = useXeroContactsCSV();
  const oAuth2Url = `https://login.xero.com/identity/connect/authorize?response_type=code&client_id=${
    import.meta.env.VITE_XERO_CLIENT_ID
  }&redirect_uri=${
    import.meta.env.VITE_URL
  }/business/settings/integrations&scope=openid profile email accounting.invoices accounting.payments accounting.settings accounting.contacts offline_access&state=Xero`;
  const [updateXeroContacts, setUpdateXeroContacts] = useState(
    business?.Supplier?.updateXeroContacts,
  );
  const [isXeroInvoicePaidViaStripe, setIsXeroInvoicePaidViaStripe] = useState(
    business?.Supplier?.isXeroInvoicePaidViaStripe,
  );

  const supplierIntegrationValueList = useSupplierIntegrationValueList(
    {
      limit: 1,
      query: [
        {
          type: "IntegrationType",
          modifier: "EQUALS",
          value: "xero",
        },
        {
          type: "key",
          modifier: "IN",
          value: ["xeroTenantName"],
        },
      ],
    },
    {
      enabled: !!business?.Supplier?.id,
      refetchOnWindowFocus: false,
    },
  );

  const hasXeroIntegration =
    !!supplierIntegrationValueList.data?.pages[0]?.[0]?.value;
  const settingsFromBusiness = business?.Supplier
    ? {
        updateContacts: business.Supplier.updateXeroContacts,
        invoicePaidViaStripe: business.Supplier.isXeroInvoicePaidViaStripe,
      }
    : undefined;
  const [previousSupplier, setPreviousSupplier] = useState(business?.Supplier);
  if (business?.Supplier !== previousSupplier) {
    setPreviousSupplier(business?.Supplier);
    setUpdateXeroContacts(settingsFromBusiness?.updateContacts);
    setIsXeroInvoicePaidViaStripe(settingsFromBusiness?.invoicePaidViaStripe);
  }

  return (
    <>
      <NavigationBar
        title={"Xero"}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={() => navigate(BUSINESS_INTEGRATION_SETTINGS)}
      />
      <PageContainer>
        <>
          <SharedIntegrationConfirmContent
            integrationType={"xero"}
            oAuth2Url={oAuth2Url}
          />
          {hasXeroIntegration ? (
            <Card className="bg-white">
              <CardHeader className={twMerge("bg-white", "p-4")}>
                <H5>Connected Xero Tenant</H5>
              </CardHeader>
              <CardContent className={"p-4"}>
                {supplierIntegrationValueList.data?.pages[0]?.[0]?.value}
              </CardContent>
            </Card>
          ) : null}
          <Card className="bg-white">
            <CardContent className={"flex flex-wrap justify-between gap-2 p-4"}>
              <div className="flex flex-col">
                <H5>Export your contact list from Xero</H5>
                <H6>
                  Exports your contact's details including the Xero contact ID.
                  Use this option if you are looking to import your customers
                  into Sellar.
                </H6>
              </div>
              <Button
                color={"secondary"}
                loading={xeroContactsCsv.isPending}
                onClick={() =>
                  xeroContactsCsv.mutate(undefined, {
                    onSuccess: (csvData) => {
                      const blob = new Blob(["\ufeff", csvData]);
                      const downloadLink = document.createElement("a");
                      const fileName = `Contacts.csv`;
                      downloadLink.href = URL.createObjectURL(blob);
                      downloadLink.download = fileName;
                      downloadLink.click();
                      downloadLink.remove();
                    },
                  })
                }
              >
                <Icon icon={"DOWNLOAD"} /> Export
              </Button>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardHeader className={twMerge("bg-white", "flex flex-col p-4")}>
              <H5>Settings</H5>
              <H6>Configure how you would like this integration to work</H6>
            </CardHeader>
            <IntegrationUpdateContacts
              updateContacts={updateXeroContacts}
              onChange={(e) => {
                const shouldUpdateContacts = e.currentTarget.value === "yes";
                updateSupplierMutation.mutate(
                  {
                    updateXeroContacts: shouldUpdateContacts,
                  },
                  {
                    onSuccess: () =>
                      setUpdateXeroContacts(shouldUpdateContacts),
                  },
                );
              }}
              integrationType={"Xero"}
            />
            <XeroStripeOption
              setIsXeroInvoicePaidViaStripe={setIsXeroInvoicePaidViaStripe}
              isXeroInvoicePaidViaStripe={isXeroInvoicePaidViaStripe}
            />
            {hasXeroIntegration ? <XeroChartOfAccountsPicker /> : null}
          </Card>
        </>
      </PageContainer>
    </>
  );
};

const XeroStripeOption = ({
  setIsXeroInvoicePaidViaStripe,
  isXeroInvoicePaidViaStripe,
}: {
  setIsXeroInvoicePaidViaStripe: (isXeroInvoicePaidViaStripe: boolean) => void;
  isXeroInvoicePaidViaStripe: boolean | undefined;
}) => {
  const { business } = useSellarCurrentBusiness();
  const updateSupplierMutation = useSupplierUpdate(business?.Supplier?.id ?? 0);
  return (
    <CardContent className={"flex flex-wrap justify-between gap-2 p-4"}>
      <InputLabel>
        For orders on Sellar where I receive payments via Stripe, apply the
        payment to the corresponding invoice on Xero
      </InputLabel>
      <RadioSelector
        onChange={(e) => {
          const invoicePaidViaStripe = e.currentTarget.value === "update";
          updateSupplierMutation.mutate(
            {
              isXeroInvoicePaidViaStripe: invoicePaidViaStripe,
            },
            {
              onSuccess: () =>
                setIsXeroInvoicePaidViaStripe(invoicePaidViaStripe),
            },
          );
        }}
        value={isXeroInvoicePaidViaStripe ? "update" : "no_update"}
        variant="grouped"
      >
        <RadioSelectorItem
          value={"update"}
          title={"Yes"}
          description={`Sellar will apply payment to Xero invoices when your customer pays by card via Stripe or when you manually mark an order as paid on Sellar before the Xero invoice gets created.`}
        />
        <RadioSelectorItem
          value={"no_update"}
          title={"No"}
          description={`Sellar will only ever create invoices on Xero in the 'draft' state.`}
        />
      </RadioSelector>
    </CardContent>
  );
};

const XeroChartOfAccountsPicker = () => {
  const { business } = useSellarCurrentBusiness();

  const updateSupplierMutation = useSupplierUpdate(business?.Supplier?.id ?? 0);

  const [accountCode, setAccountCode] = useState(
    business?.Supplier?.xeroAccountCode ?? "",
  );

  const accounts = useXeroChartOfAccountsList({});

  if (accounts.isLoading)
    return (
      <div className={"flex w-full flex-col items-center py-4"}>
        <ButtonSpinner />
      </div>
    );

  if (accounts.error) {
    return <div>Something went wrong.</div>;
  }

  return (
    <CardContent
      className={"flex flex-col flex-wrap justify-between gap-2 p-4"}
    >
      <InputLabel>
        Which Xero account do you want Sellar orders to be entered into?
      </InputLabel>
      {accounts.isLoading ? (
        <div className={"flex w-full justify-center"}>
          <ButtonSpinner />
        </div>
      ) : (
        <InputContainer>
          <InputSelect
            value={accountCode}
            placeholder={"No Xero account selected"}
            onChange={(e) => {
              const newAccountCode = e.currentTarget.value;
              setAccountCode(newAccountCode);
              updateSupplierMutation.mutate({
                xeroAccountCode: e.currentTarget.value,
              });
            }}
          >
            <option value="" disabled={true} className={"hidden"}>
              None selected
            </option>
            {accounts.data?.pages.map((page) =>
              page.accounts.map((account) => (
                <option key={account.code} value={account.code}>
                  {account.name}
                </option>
              )),
            )}
          </InputSelect>
        </InputContainer>
      )}
    </CardContent>
  );
};
